import { useState, useRef, useEffect } from "react";
import { useTableFilterContext } from "src/context";
import { useClickOutsideModalListener } from "src/hooks/useClickOutsideModalListener";
import { ButtonDropdownAnchor } from "./FilterControls.styles";
import { StyledInput, ButtonLabelText } from "./TextInput.styles";
import { Dropdown } from "./Dropdown";
import { SearchIcon } from "src/assets/icons";
import { GhostButton } from "src/components/Buttons";

const getPlaceholderForFindSelection = (key: string): string => {
  if (key === "first_name")
    return "Find by first name (exact match only e.g. 'Joe')";
  else if (key === "last_name")
    return "Find by last name (exact match only e.g. 'Smith')";
  else if (key === "application_id") return "Find by ID";
  else return "Please select a key to search by";
};

const getFindInputSelectionText = (key: string, text: string | null) => {
  if (key === "first_name")
    return (
      <ButtonLabelText>
        First name contains &apos;<strong>{text}</strong>&apos;
      </ButtonLabelText>
    );
  else if (key === "last_name")
    return (
      <ButtonLabelText>
        Last name contains &apos;<strong>{text}</strong>&apos;
      </ButtonLabelText>
    );
  else if (key === "application_id")
    return (
      <ButtonLabelText>
        Application ID contains &apos;<strong>{text}</strong>&apos;
      </ButtonLabelText>
    );
  else return "Please select a key to search by";
};

interface TextInputProps {
  findSelection: string;
  onClose: () => void;
}

export default ({ findSelection, onClose }: TextInputProps) => {
  const { textSearchValue, setTextSearchField, setTextSearchValue } =
    useTableFilterContext();
  const [findSearchText, setFindSearchText] = useState<string | null>(null);
  const findSearchTextInputRef = useRef(null);
  useClickOutsideModalListener(findSearchTextInputRef, onClose);
  const inputRef = useRef<HTMLInputElement>(null);
  const showFindTextInput = Boolean(
    findSelection &&
    findSelection.length > 0 &&
    (!textSearchValue || textSearchValue.length === 0),
  );
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [showFindTextInput]);

  const onSelect = () => {
    setFindSearchText(null);
    setTextSearchField(findSelection);
    setTextSearchValue(findSearchText);
    onClose();
  };

  if (!showFindTextInput) {
    return null;
  }

  return (
    <ButtonDropdownAnchor ref={findSearchTextInputRef}>
      <StyledInput
        ref={inputRef}
        value={findSearchText || ""}
        onChange={(evt) => {
          const characterLimit = 50;
          const limitedText = evt.target.value.slice(0, characterLimit);
          setFindSearchText(limitedText);
        }}
        onKeyUp={(evt) => {
          if (evt.key === "Enter") {
            onSelect();
          }
        }}
        placeholder={getPlaceholderForFindSelection(findSelection)}
      />
      <Dropdown clickHandleRef={null} show={(findSearchText?.length ?? 0) > 0}>
        <GhostButton
          onClick={() => {
            onSelect();
          }}
        >
          <SearchIcon />
          {getFindInputSelectionText(findSelection, findSearchText)}
        </GhostButton>
      </Dropdown>
    </ButtonDropdownAnchor>
  );
};
